import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Badge,
  BlockStack,
  InlineStack,
  Text,
  SkeletonBodyText,
  Box,
  Divider,
  DataTable,
  TextField,
  Button,
  InlineError,
} from '@shopify/polaris';
import { assetsApi } from '@/services';
import { useAppBridge } from '@shopify/app-bridge-react';
import type { AssetStatus, AssetTransaction } from '@/types';

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const shopify = useAppBridge();

  // GPS inline-edit state
  const [gpsEditing, setGpsEditing] = useState(false);
  const [gpsDraft, setGpsDraft] = useState('');

  const { data: asset, isLoading } = useQuery({
    queryKey: ['asset', id],
    queryFn: () => assetsApi.getById(id!),
    enabled: !!id,
  });

  const gpsMutation = useMutation({
    mutationFn: (gpsId: string | null) => assetsApi.updateGpsId(id!, gpsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      setGpsEditing(false);
      shopify.toast.show('GPS ID updated');
    },
  });

  const handleGpsEdit = () => {
    setGpsDraft(asset?.gpsId ?? '');
    setGpsEditing(true);
  };

  const handleGpsSave = () => {
    const trimmed = gpsDraft.trim();
    gpsMutation.mutate(trimmed === '' ? null : trimmed);
  };

  const handleGpsCancel = () => {
    setGpsEditing(false);
    setGpsDraft('');
  };

  const getStatusBadge = (status: AssetStatus) => {
    const statusConfig: Record<AssetStatus, { tone: 'success' | 'warning' | 'critical' | 'info' | undefined; label: string }> = {
      ready: { tone: 'success', label: 'Ready' },
      allocated: { tone: 'info', label: 'Allocated' },
      in_use: { tone: undefined, label: 'In Use' },
      qc_hold: { tone: 'warning', label: 'QC Hold' },
      repair: { tone: 'critical', label: 'Repair' },
      repair_complete: { tone: 'info', label: 'Repair Complete' },
      at_clinic: { tone: undefined, label: 'At Clinic' },
      retired: { tone: 'critical', label: 'Retired' },
    };
    const config = statusConfig[status] || { tone: undefined, label: status };
    return <Badge tone={config.tone}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <Page title="Asset Details">
        <Card>
          <SkeletonBodyText lines={10} />
        </Card>
      </Page>
    );
  }

  if (!asset) {
    return (
      <Page
        title="Asset Not Found"
        backAction={{ content: 'Back', onAction: () => navigate('/assets') }}
      >
        <Card>
          <Text as="p">The requested asset could not be found.</Text>
        </Card>
      </Page>
    );
  }

  const transactionRows = (asset.transactions || []).map((tx: AssetTransaction) => [
    new Date(tx.createdAt).toLocaleString(),
    tx.transactionType,
    tx.previousStatus || 'N/A',
    tx.newStatus,
    tx.notes || '-',
    tx.performedBy || 'System',
  ]);

  return (
    <Page
      title={`Asset: ${asset.serialNumber}`}
      backAction={{ content: 'Back', onAction: () => navigate('/assets') }}
      titleMetadata={getStatusBadge(asset.assetStatus)}
    >
      <Layout>
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Asset Information
              </Text>
              <Divider />
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Serial Number</Text>
                  <Text as="span" fontWeight="semibold">{asset.serialNumber}</Text>
                </InlineStack>
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="span" tone="subdued">GPS ID</Text>
                  {gpsEditing ? (
                    <InlineStack gap="200" blockAlign="center">
                      <TextField
                        label=""
                        labelHidden
                        value={gpsDraft}
                        onChange={setGpsDraft}
                        autoComplete="off"
                        placeholder="GPS tracker ID"
                      />
                      <Button
                        size="slim"
                        variant="primary"
                        onClick={handleGpsSave}
                        loading={gpsMutation.isPending}
                      >
                        Save
                      </Button>
                      <Button size="slim" onClick={handleGpsCancel}>Cancel</Button>
                    </InlineStack>
                  ) : (
                    <InlineStack gap="200" blockAlign="center">
                      <Text as="span" fontWeight="semibold">
                        {asset.gpsId ?? '—'}
                      </Text>
                      <Button size="slim" variant="plain" onClick={handleGpsEdit}>
                        Edit
                      </Button>
                    </InlineStack>
                  )}
                </InlineStack>
                {gpsMutation.isError && (
                  <InlineError message="Failed to update GPS ID" fieldID="gpsId" />
                )}
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">SKU</Text>
                  <Text as="span">{asset.productSku}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Product</Text>
                  <Text as="span">{asset.productName}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Status</Text>
                  {getStatusBadge(asset.assetStatus)}
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Current Rental</Text>
                  <Text as="span">
                    {asset.currentRentalId ? (
                      <a href={`/rentals/${asset.currentRentalId}`}>View Rental</a>
                    ) : (
                      'None'
                    )}
                  </Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Created</Text>
                  <Text as="span">{new Date(asset.createdAt).toLocaleDateString()}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Last Updated</Text>
                  <Text as="span">{new Date(asset.updatedAt).toLocaleDateString()}</Text>
                </InlineStack>
              </BlockStack>
              {asset.notes && (
                <>
                  <Divider />
                  <BlockStack gap="200">
                    <Text as="span" tone="subdued">Notes</Text>
                    <Text as="p">{asset.notes}</Text>
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Transaction History
              </Text>
              {transactionRows.length === 0 ? (
                <Box padding="400">
                  <Text as="p" tone="subdued" alignment="center">
                    No transactions recorded
                  </Text>
                </Box>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Date', 'Type', 'From', 'To', 'Notes', 'By']}
                  rows={transactionRows}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
